import { useState } from "react";
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { Flex } from "src/components/Flex.tsx";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";

import type { GridRenderHeaderFilterProps } from "@mui/x-data-grid-premium";
import type { SelectProps } from "src/components/SelectV2/SelectV2.types.ts";
import type {
  HeaderFilterSelectOptions,
  HeaderFilterSelectServerOptions,
} from "src/components/DataGrid/DataGrid.tsx";

import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { arrReplaceAt, trimInfiniteQuery } from "@exonn/utils";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import { useInfiniteSelect } from "src/system/useInfiniteSelect.ts";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@exonn/hooks";
import { GridLogicOperator } from "@mui/x-data-grid";

const DEFAULT_PAGE_SIZE = 15;

export const DataGridInfiniteSelectHeaderFilterImpl = observer(
  (
    props: GridRenderHeaderFilterProps &
      Pick<
        SelectProps<string>,
        "placeholder" | "allowClearValue" | "optionsMinWidth"
      > & {
        options?: HeaderFilterSelectOptions;
        serverOptions?: HeaderFilterSelectServerOptions;
      },
  ) => {
    const {
      item,
      options: optionsProp,
      placeholder,
      allowClearValue,
      serverOptions,
      optionsMinWidth,
    } = props;
    const apiRef = useGridApiContext();
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation(TranslationNamespaceEnum.DataGridComponent);
    const store = useDataGridStore();
    const queryClient = useQueryClient();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );
    const rootProps = useGridRootProps();

    const [search, setSearch] = useState<string>("");
    const [debouncedSearch] = useDebouncedValue(search, 500);

    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

    const getDisplayValue = () => {
      if (item.value != null) {
        return String(item.value);
      }
      if (appliedItem?.value != null) {
        return String(appliedItem.value);
      }

      return "";
    };

    const value = getDisplayValue();
    const hasValue = !!value;

    const infiniteSelect = useInfiniteSelect({
      search: debouncedSearch,
      searchField: serverOptions?.searchField,
      resource: serverOptions ? serverOptions.resource : "",
      sort: serverOptions?.sort,
      include: serverOptions?.include,
      labelProp: serverOptions?.labelProp,
      valueProp: serverOptions?.valueProp,
      useSimplePagination: true,
      filter:
        serverOptions?.filter || serverOptions?.logicOperator
          ? {
              logicOperator: serverOptions?.logicOperator
                ? serverOptions.logicOperator
                : GridLogicOperator.And,
              items: serverOptions?.filter || [],
            }
          : undefined,
      pageSize: DEFAULT_PAGE_SIZE,
      queryOptions: {
        enabled: !!serverOptions && isOpen,
        refetchOnMount: false,
        gcTime: 0,
        staleTime: 0,
        placeholderData: keepPreviousData,
      },
    });

    const handleSelect = (value?: string | number) => {
      const nextItem = {
        ...item,
        value: value,
      };
      const mergedFilterModel = mergeFilterModels(
        filterModel,
        store.appliedFilterModel,
      );
      const prevItems = mergedFilterModel.items;

      const nextItems = arrReplaceAt(
        prevItems,
        (prevItem) => prevItem.field === nextItem.field,
        nextItem,
        true,
      );

      const filteredItems = nextItems.filter((item) => !!item.value);

      const nextFilterModel = {
        ...mergedFilterModel,
        items: filteredItems,
      };

      if (rootProps.filterMode === "server") {
        //applyFilterChanges(nextItem);
        apiRef.current.setFilterModel({
          items: [],
        });
        store.setAppliedFilterModel(nextFilterModel);
      } else {
        apiRef.current.setFilterModel(nextFilterModel);
      }
    };

    const options = serverOptions
      ? serverOptions.processOptions
        ? serverOptions.processOptions(infiniteSelect.options)
        : infiniteSelect.options
      : Array.isArray(optionsProp)
      ? optionsProp
      : optionsProp &&
        getResourceFieldOptions({
          ...optionsProp,
          t,
        });

    const handleOpenChange = (open: boolean) => {
      setIsOpen(open);
      if (open && serverOptions) {
        trimInfiniteQuery(queryClient, infiniteSelect.queryKey);
        infiniteSelect.query.refetch();

        if (hasValue) {
          infiniteSelect.query.refetch();
        }
      }
    };

    const handleScrollEnd = () => {
      if (!infiniteSelect.query.isFetchingNextPage) {
        infiniteSelect.query.fetchNextPage({
          cancelRefetch: false,
        });
      }
    };

    const isLoadingData = serverOptions
      ? infiniteSelect.query.isFetching &&
        !infiniteSelect.query.isFetchingNextPage
      : false;

    const handleSearchChange = (value: string) => {
      const queryKey = infiniteSelect.getQueryKey({
        search: value,
      });
      trimInfiniteQuery(queryClient, queryKey);
      setSearch(value);
    };

    return (
      <Flex style={styles.container}>
        <SelectV2
          allowClearValue={allowClearValue}
          onChange={handleSelect}
          onOpenChange={handleOpenChange}
          // isLoadingSelectedOption={isLoadingServerOptions}
          isLoadingData={isLoadingData}
          placeholder={placeholder ?? t("header-filters.select-placeholder")}
          value={value}
          options={options}
          optionsMinWidth={optionsMinWidth}
          showSearch={true}
          isLoadingMoreOptions={infiniteSelect.query.isFetchingNextPage}
          onScrollEnd={handleScrollEnd}
          searchValue={search}
          onSearchValueChange={handleSearchChange}
          showAdvancedSearch={false}
        />
      </Flex>
    );
  },
);

const styles = {
  container: {
    // 1px padding is needed to not cut off input outline
    padding: "1px 10px 10px",
    width: "100%",
    height: "100%",
  },
};
